import React, { useState } from "react";
import { Info, Phone } from "lucide-react";
import { Helmet } from 'react-helmet-async';
import img from "../Images/plate.jpg"
import img1 from "../Images/abb.png"
import img2 from "../Images/deluge.jpeg"
import img3 from "../Images/airh.jpeg"
import img4 from "../Images/orif.webp"
import img5 from "../Images/rupd.jpg"
import img7 from "../Images/pressure.jpg"
import img8 from "../Images/flash.jpg"
import img9 from "../Images/airvent.jpeg"
import img10 from "../Images/anglev.jpg"
import img11 from "../Images/prv.jpg"
import img12 from "../Images/csr.webp"
import img22 from "../Images/refuel.webp"
import img13 from "../Images/plate.png";
import img14 from "../Images/air.png";
import img15 from "../Images/header.png";
import img16 from "../Images/orifice.png";
import img17 from "../Images/rup.png";
import img18 from "../Images/ref.png";
import img19 from "../Images/liq.png";
import img20 from "../Images/airvent.png";
import img21 from "../Images/pressurer.png";

const ProductCard = ({ product }) => {
  const [activeTab, setActiveTab] = useState('description');

  return (
    <article 
      className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 p-4 sm:p-6 lg:p-8"
      itemScope 
      itemType="https://schema.org/Product"
    >
      <header className="mb-4">
        <h3 className="text-lg sm:text-xl lg:text-2xl font-bold mb-2 text-gray-900" itemProp="name">
          {product.name}
        </h3>
      </header>
      
      <div className="bg-gray-100 h-48 sm:h-56 md:h-64 lg:h-72 mb-4 rounded-lg overflow-hidden">
        <img 
          src={product.mainImage}
          alt={`${product.name} - High-quality industrial equipment by Gortherm Energy`}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          itemProp="image"
          loading="lazy"
          width="600"
          height="400"
        />
      </div>

      <nav className="mb-4">
        <div className="flex border-b border-gray-200" role="tablist" aria-label="Product information">
          <button
            className={`flex-1 py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm lg:text-base transition-colors ${
              activeTab === 'description'
                ? 'border-b-2 border-teal-600 text-teal-600 font-semibold'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
            onClick={() => setActiveTab('description')}
            aria-selected={activeTab === 'description'}
            role="tab"
            aria-controls="description-panel"
          >
            Description
          </button>
          <button
            className={`flex-1 py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm lg:text-base transition-colors ${
              activeTab === 'specs'
                ? 'border-b-2 border-teal-600 text-teal-600 font-semibold'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
            onClick={() => setActiveTab('specs')}
            aria-selected={activeTab === 'specs'}
            role="tab"
            aria-controls="specs-panel"
          >
            <span className="hidden sm:inline">Technical Specifications</span>
            <span className="sm:hidden">Specs</span>
          </button>
        </div>
      </nav>

      <div className="mt-4" role="tabpanel" id={activeTab === 'description' ? 'description-panel' : 'specs-panel'}>
        {activeTab === 'description' ? (
          <p className="text-gray-700 text-justify text-sm sm:text-base leading-relaxed" itemProp="description">
            {product.description}
          </p>
        ) : (
          <div>
            {product.specImage && (
              <div className="bg-gray-100 h-48 sm:h-56 md:h-64 rounded-lg overflow-hidden mb-4">
                <img 
                  src={product.specImage}
                  alt={`${product.name} detailed technical specifications and measurements`}
                  className="w-full h-full object-contain p-2"
                  loading="lazy"
                  width="600"
                  height="400"
                />
              </div>
            )}
            <ul className="space-y-2 sm:space-y-3">
              {product.specifications.map((spec, index) => (
                <li key={index} className="flex items-start">
                  <Info className="h-4 w-4 sm:h-5 sm:w-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <span className="text-gray-700 text-sm sm:text-base">{spec}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </article>
  );
};

const Others = () => {
  const products = [
    {
      id: 'plate-heat-exchanger',
      name: 'Plate Heat Exchanger',
      mainImage: img,
      specImage: img13,
      description: `Our Plate Heat Exchangers represent the pinnacle of thermal transfer technology, offering exceptional heat transfer efficiency in a compact design. These units feature precisely engineered plates that maximize heat transfer while minimizing space requirements. The innovative plate design ensures optimal flow distribution and thermal performance across a wide range of applications, from HVAC systems to industrial processes.`,
      specifications: [
        'Heat transfer efficiency up to 95%',
        'Operating pressure up to 25 bar',
        'Temperature range: -40°C to 180°C',
        'Plate material: AISI 316L stainless steel',
        'Gasket materials: NBR, EPDM, Viton',
        'Frame types: Fixed and portable designs',
        'Multiple connection options available',
        'Easy maintenance access design'
      ]
    },
    {
      id: 'blow-down-system',
      name: 'Boiler Automatic Blow-Down System',
      mainImage: img1,
      specImage: img14,
      description: `The Boiler Automatic Blow-Down System provides precise control of boiler water chemistry through automated surface and bottom blow-down operations. This system helps maintain optimal boiler efficiency while preventing scale formation and corrosion. The automated operation ensures consistent water quality management while reducing operator intervention and manual handling risks.`,
      specifications: [
        'Fully automated operation with PLC control',
        'TDS control range: 0-10,000 ppm',
        'Operating pressure up to 32 bar',
        'Temperature capability up to 240°C',
        'Multi-boiler management capability',
        'Integrated data logging and trending',
        'Remote monitoring and control options',
        'Energy recovery system compatible'
      ]
    },
    {
      id: 'deluge-valve',
      name: 'Deluge Valve',
      mainImage: img2,
      specImage: img14,
      description: `Our Deluge Valves are engineered for reliable fire protection system operation, providing rapid response in emergency situations. These valves feature a robust design that ensures consistent operation under demanding conditions. The system includes comprehensive monitoring capabilities and can be integrated with various fire detection and alarm systems for automated operation.`,
      specifications: [
        'Response time < 1 second',
        'Working pressure: Up to 16 bar',
        'Flow rates up to 20,000 L/min',
        'UL/FM approved designs',
        'Manual and automatic activation',
        'Multiple trim configurations available',
        'Corrosion-resistant construction',
        'Low maintenance requirements'
      ]
    },
    {
      id: 'air-header',
      name: 'Air Header',
      mainImage: img3,
      specImage: img15,
      description: `Industrial Air Headers designed for efficient distribution of compressed air throughout facility systems. Our headers feature optimized flow paths that minimize pressure drops while ensuring even distribution. The robust construction and strategic port placement enable flexible system integration and future expansion capabilities.`,
      specifications: [
        'Working pressure up to 40 bar',
        'Material: Carbon steel or stainless steel',
        'Multiple port configurations available',
        'ASME Section VIII compliant design',
        'Custom sizes and layouts available',
        'Pressure tested to 1.5x working pressure',
        'Surface treatment options available',
        'Installation support included'
      ]
    },
    {
      id: 'orifice-plate',
      name: 'Orifice Plate',
      mainImage: img4,
      specImage: img16,
      description: `High-precision Orifice Plates manufactured for accurate flow measurement in various industrial applications. Our orifice plates are precision-machined to ensure accurate flow measurement and feature various edge designs to suit different applications. Each plate is individually calibrated and comes with comprehensive documentation.`,
      specifications: [
        'Accuracy: ±0.5% of measured value',
        'Beta ratios: 0.2 to 0.75',
        'Size range: DN15 to DN1000',
        'Material options: SS316, SS304, Hastelloy',
        'ISO 5167 compliant design',
        'Custom bore calculations available',
        'Bi-directional flow capability',
        'Various tab configurations'
      ]
    },
    {
      id: 'rupture-disc',
      name: 'Rupture Disc',
      mainImage: img5,
      specImage: img17,
      description: `Advanced Rupture Discs designed for reliable overpressure protection in critical process applications. These precision-engineered safety devices provide instantaneous response to overpressure conditions, protecting valuable equipment and ensuring personnel safety. Our rupture discs feature innovative designs that maximize service life and reliability.`,
      specifications: [
        'Burst pressure accuracy: ±5%',
        'Operating ratio up to 95%',
        'Temperature range: -196°C to 450°C',
        'Size range: DN25 to DN900',
        'Non-fragmenting designs available',
        'ASME UD stamp certified',
        'Multiple material options',
        'Leak-tight to point of rupture'
      ]
    },
    {
      id: 'auto-recirculation-valve',
      name: 'Auto Re-circulation / Refueller Valve',
      mainImage: img22,
      specImage: img18,
      description: `Innovative Auto Re-circulation Valves designed to protect pumps and ensure system stability. These valves automatically maintain minimum flow requirements for pump protection while optimizing system efficiency. The integrated design combines multiple functions in a single unit, reducing installation complexity and maintenance requirements.`,
      specifications: [
        'Flow range: 1 to 1000 m³/hr',
        'Pressure class: up to ANSI 600',
        'Response time < 0.5 seconds',
        'Self-operating design',
        'Integrated check valve function',
        'Custom sizing available',
        'Low pressure drop design',
        'Multiple connection options'
      ]
    },
    {
      id: 'prv-water',
      name: 'Pressure Reducing Valves for Water/Liquid Applications',
      mainImage: img7,
      specImage: img19,
      description: `Specialized Pressure Reducing Valves engineered for precise pressure control in water and liquid applications. These valves feature advanced pressure sensing and control mechanisms to maintain stable downstream pressure regardless of upstream pressure fluctuations. The robust design ensures reliable operation with minimal maintenance requirements.`,
      specifications: [
        'Pressure reduction ratio up to 10:1',
        'Size range: DN15 to DN300',
        'Pressure rating up to PN25',
        'Accuracy: ±1% of setpoint',
        'Integral strainer option',
        'Low noise trim available',
        'Anti-cavitation designs',
        'Various control options'
      ]
    },
    {
      id: 'flash-vessel',
      name: 'Flash Vessel',
      mainImage: img8,
      specImage: "",
      description: `Industrial Flash Vessels designed for efficient separation of flash steam from condensate. Our vessels feature optimized internal geometry for maximum separation efficiency and minimum pressure drop. The robust construction and comprehensive instrumentation options ensure reliable operation in demanding industrial environments.`,
      specifications: [
        'Operating pressure up to 32 bar',
        'Temperature rating up to 250°C',
        'Vertical and horizontal designs',
        'ASME Section VIII construction',
        'Multiple nozzle configurations',
        'Level control options available',
        'Insulation jacket ready',
        'Custom sizing available'
      ]
    },
    {
      id: 'air-vents',
      name: 'Air Vents',
      mainImage: img9,
      specImage: img20,
      description: `Advanced Air Vents designed for efficient removal of air and non-condensable gases from steam and liquid systems. These vents feature innovative float mechanisms that ensure reliable operation and prevent steam loss. The compact design allows for installation in space-constrained areas while maintaining high performance.`,
      specifications: [
        'Operating pressure up to 21 bar',
        'Temperature capability to 220°C',
        'Discharge capacity up to 2000 L/hr',
        'Stainless steel construction',
        'Integral strainer option',
        'Balanced pressure operation',
        'Freeze protection available',
        'In-line serviceable design'
      ]
    },
    {
      id: 'angle-valve',
      name: 'Angle Valve',
      mainImage: img10,
      specImage: "",
      description: `Heavy-duty Angle Valves engineered for demanding industrial applications. These valves feature robust construction and precise flow control capabilities, making them ideal for high-pressure and high-temperature applications. The angle design provides excellent flow characteristics while minimizing space requirements.`,
      specifications: [
        'Pressure class up to ANSI 2500',
        'Temperature range: -196°C to 550°C',
        'Size range: DN15 to DN300',
        'Various trim materials available',
        'Hard-faced seat options',
        'Extended bonnet designs',
        'Fire-safe construction available',
        'Custom actuation options'
      ]
    },
    {
      id: 'pressure-reducing-valves',
      name: 'Pressure Reducing Valves',
      mainImage: img11,
      specImage: img21,
      description: `Industrial Pressure Reducing Valves designed for precise pressure control in steam and gas applications. These valves incorporate advanced pressure sensing and control mechanisms to maintain stable downstream pressure under varying flow conditions. The robust construction ensures long-term reliability in demanding industrial environments.`,
      specifications: [
        'Pressure reduction ratio up to 20:1',
        'Size range: DN15 to DN200',
        'Temperature rating up to 400°C',
        'Self-operating design',
        'Noise reduction trim available',
        'Integral pressure sensing',
        'Multiple spring ranges',
        'NACE compliance available'
      ]
    },
    {
      id: 'slide-gate-valves',
      name: 'Slide Gate Valves Condensate Recovery System (CRS)',
      mainImage: img12,
      specImage: "/ProdSpec/SGV_Spec.jpg",
      description: `Advanced Slide Gate Valves specifically designed for condensate recovery systems. These valves feature innovative gate designs that ensure tight shutoff and minimal wear under frequent cycling. The integrated design includes features for optimal condensate handling and system protection.`,
      specifications: [
        'Working pressure up to 40 bar',
        'Temperature rating to 400°C',
        'Zero-leakage design',
        'Automated operation capability',
        'Position indication standard',
        'Self-cleaning action',
        'Bidirectional sealing',
        'Low maintenance design'
      ]
    }
  ];

  // Generate structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Industrial Equipment & Solutions",
    "description": "Comprehensive range of industrial equipment including heat exchangers, valves, vessels, and control systems",
    "itemListElement": products.map((product, index) => ({
      "@type": "Product",
      "position": index + 1,
      "name": product.name,
      "description": product.description,
      "image": product.mainImage,
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock",
        "seller": {
          "@type": "Organization",
          "name": "Gortherm Energy"
        }
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Industrial Equipment & Solutions | Heat Exchangers, Valves & Systems | Gortherm Energy</title>
        <meta name="description" content="Explore Gortherm Energy's comprehensive range of industrial equipment: plate heat exchangers, pressure reducing valves, deluge valves, rupture discs, air vents, and automated boiler systems. High-quality solutions for industrial applications." />
        <meta name="keywords" content="plate heat exchanger, pressure reducing valve, deluge valve, rupture disc, orifice plate, air header, flash vessel, air vents, angle valve, boiler blow-down system, industrial valves, heat transfer equipment, condensate recovery system, industrial equipment Nepal" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Industrial Equipment & Solutions | Gortherm Energy" />
        <meta property="og:description" content="High-quality industrial equipment including heat exchangers, valves, vessels, and control systems for demanding industrial applications." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.gorthermenergy.com/industrial-equipment" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Industrial Equipment & Solutions | Gortherm Energy" />
        <meta name="twitter:description" content="Comprehensive range of industrial equipment for thermal and pressure management applications." />
        
        {/* Additional SEO */}
        <link rel="canonical" href="https://www.gorthermenergy.com/industrial-equipment" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="author" content="Gortherm Energy" />
        <meta name="language" content="English" />
        <meta name="geo.region" content="NP" />
        <meta name="geo.placename" content="Nepal" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <main className="pt-16 sm:pt-20 min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
          <header className="text-center mb-12 sm:mb-16 lg:mb-20">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
              Industrial Equipment & Solutions
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Discover our comprehensive range of industrial equipment and solutions, 
              engineered to meet the demanding requirements of modern industrial processes.
            </p>
          </header>

          <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-10">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </section>

          <aside className="mt-12 sm:mt-16 lg:mt-20 bg-gradient-to-r from-teal-50 to-blue-50 rounded-2xl p-6 sm:p-8 lg:p-12 shadow-lg">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Need Expert Guidance?
              </h2>
              <p className="text-base sm:text-lg text-gray-700 mb-6 sm:mb-8 px-4">
                Our specialists are here to help you select the perfect industrial equipment 
                solution for your specific application requirements.
              </p>
              <a 
                href="tel:+9779802781400"
                className="inline-block"
                aria-label="Call Gortherm Energy experts at +977 980 278 1400"
              >
                <button className="bg-teal-600 hover:bg-teal-700 active:bg-teal-800 text-white px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-lg flex items-center gap-2 sm:gap-3 mx-auto transition-all duration-300 shadow-md hover:shadow-xl text-sm sm:text-base lg:text-lg font-semibold">
                  <Phone className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
                  <span>Contact Our Experts</span>
                </button>
              </a>
              
            </div>
          </aside>
        </div>
      </main>
    </>
  );
};

export default Others;